import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div style={styles.page}>
      <Helmet>
        <title>Privacy Policy | Forecast</title>
        <meta name="description" content="Read Forecast's privacy policy. Learn how we protect your financial data and what information we collect." />
        <link rel="canonical" href="https://tryforecast.app/privacy-policy" />
      </Helmet>

      <div style={styles.container}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          <span style={styles.backArrow}>&larr;</span> Back
        </button>

        <div style={styles.header}>
          <div style={styles.badge}>Privacy</div>
          <h1 style={styles.title}>Privacy Policy</h1>
          <p style={styles.date}>Effective February 23, 2025 &middot; Updated April 5, 2026</p>
        </div>

        <div style={styles.divider} />

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>1. Introduction</h2>
          <p style={styles.text}>
            Forecast ("we," "our," or "us") respects your privacy and is committed to protecting it through this Privacy Policy.
            This policy explains how we collect, use, and protect your personal information when you use our app.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>2. Information We Collect</h2>
          <p style={styles.text}>We may collect the following types of data when you use Forecast:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Personal Information:</strong> Name, email, and financial details (if entered manually by you).</li>
            <li style={styles.listItem}><strong>Usage Data:</strong> Analytics on how you interact with the app to improve the experience.</li>
            <li style={styles.listItem}><strong>Device Information:</strong> Device type, operating system, and unique device identifiers.</li>
            <li style={styles.listItem}><strong>Payment Information:</strong> For subscription management, we use Stripe to process payments. We do not store payment details directly.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>3. How We Use Your Information</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>Provide and improve our financial planning services.</li>
            <li style={styles.listItem}>Personalize your experience within the app.</li>
            <li style={styles.listItem}>Process and manage subscription transactions.</li>
            <li style={styles.listItem}>Send important app updates or security notifications.</li>
            <li style={styles.listItem}>Analyze app usage to improve functionality and user experience.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>4. Subscription Information</h2>
          <p style={styles.text}>Our app offers auto-renewable subscriptions with the following terms:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Premium Access is available as a monthly ($6.70/mo) or annual ($53.99/yr) subscription.</li>
            <li style={styles.listItem}>Payment is processed through Stripe at confirmation of purchase.</li>
            <li style={styles.listItem}>Subscription automatically renews unless canceled before the end of the current period.</li>
            <li style={styles.listItem}>You can manage and cancel your subscription through the app or by contacting support.</li>
            <li style={styles.listItem}>Any unused portion of a free trial period will be forfeited when you purchase a subscription.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>5. Data Sharing & Third-Party Services</h2>
          <p style={styles.text}>We do not sell, rent, or share your personal or financial data with third parties for marketing purposes. We use the following third-party services strictly to operate the app:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Plaid</strong> (for secure bank account linking) — If you choose to link a bank account, Plaid handles the connection directly. Your bank login credentials are never sent to or stored on our servers. Plaid is the same service used by Venmo, Coinbase, and other major fintech apps. You can review <a href="https://plaid.com/legal/#end-user-privacy-policy" style={styles.link} target="_blank" rel="noopener noreferrer">Plaid's Privacy Policy</a> for more details.</li>
            <li style={styles.listItem}><strong>Stripe</strong> (for subscription payment processing) — We never store your credit card or payment details directly.</li>
            <li style={styles.listItem}><strong>Firebase</strong> (Google Cloud) — Used for authentication, database, and hosting. Data is encrypted at rest and in transit.</li>
            <li style={styles.listItem}><strong>Analytics providers</strong> — To improve app performance and user experience.</li>
          </ul>
          <p style={styles.text}>These third parties have their own privacy policies, which we encourage you to review. Bank linking is entirely optional — many users prefer manual entry.</p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>6. Data Storage & Retention</h2>
          <p style={styles.text}>
            We store your data securely in the cloud using Firebase. Your personal information is retained for as long as your account is active.
            If you delete your account, we will delete your personal information within 30 days, except where we have a legal obligation to retain it.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>7. Data Protection & Security</h2>
          <p style={styles.text}>
            We take security seriously. Your data is protected with TLS encryption in transit and encrypted at rest on Google Cloud (Firebase) infrastructure. Authentication is handled via Firebase Auth with secure token-based sessions. Bank linking through Plaid uses bank-level AES-256 encryption, and your bank credentials are never accessible to us. However, no method of transmission over the internet is 100% secure.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>8. Your Rights & Choices</h2>
          <p style={styles.text}>You can:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Access, update, or delete your data</strong> by contacting us or using the app's account settings.</li>
            <li style={styles.listItem}><strong>Opt-out of analytics tracking</strong> in your app settings.</li>
            <li style={styles.listItem}><strong>Manage subscriptions</strong> through the app or by contacting support.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>9. Children's Privacy</h2>
          <p style={styles.text}>
            Our app is not directed at children under the age of 13. We do not knowingly collect personal information from children under 13.
            If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>10. California Privacy Rights</h2>
          <p style={styles.text}>
            California residents have certain rights regarding their personal information under the California Consumer Privacy Act (CCPA).
            You have the right to know what personal information we collect, request deletion of your data, and opt-out of the sale of personal information.
            We do not sell your personal information.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>11. International Users</h2>
          <p style={styles.text}>
            Our services are hosted in the United States. If you access our app from outside the United States,
            please be aware that your information may be transferred to, stored, and processed in the United States.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>12. Updates to This Privacy Policy</h2>
          <p style={styles.text}>
            We may update this policy periodically. The date at the top of this policy indicates when it was last revised.
            Any changes will be effective immediately upon posting the revised policy. We will notify you of significant changes through the app or by email.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>13. Contact Us</h2>
          <p style={styles.text}>If you have questions about this Privacy Policy, you can contact us at:</p>
          <div style={styles.contactCard}>
            <p style={styles.contactItem}>
              <span style={styles.contactLabel}>Email</span>
              <a href="mailto:r.dirawatun@gmail.com" style={styles.link}>r.dirawatun@gmail.com</a>
            </p>
            <p style={styles.contactItem}>
              <span style={styles.contactLabel}>Website</span>
              <a href="https://tryforecast.app" style={styles.link} target="_blank" rel="noopener noreferrer">tryforecast.app</a>
            </p>
          </div>
        </section>

        <div style={styles.footer}>
          <p style={styles.footerText}>Forecast &middot; San Diego, CA</p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #F8FAFC 0%, #EEF2FF 100%)',
    padding: '24px 16px 60px',
  },
  container: {
    maxWidth: 720,
    margin: '0 auto',
    background: '#FFFFFF',
    borderRadius: 24,
    padding: '40px 36px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 32px rgba(0,0,0,0.06)',
  },
  backButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 15,
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 500,
    color: '#6366F1',
    padding: '8px 0',
    marginBottom: 24,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    transition: 'opacity 0.2s',
  },
  backArrow: {
    fontSize: 18,
  },
  header: {
    textAlign: 'center',
    marginBottom: 8,
  },
  badge: {
    display: 'inline-block',
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 600,
    fontSize: 13,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#6366F1',
    background: 'rgba(99,102,241,0.08)',
    padding: '6px 16px',
    borderRadius: 20,
    marginBottom: 16,
  },
  title: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 800,
    fontSize: 36,
    color: '#0F172A',
    margin: '0 0 12px',
    letterSpacing: '-0.02em',
    lineHeight: 1.15,
  },
  date: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 400,
    fontSize: 14,
    color: '#94A3B8',
    margin: 0,
  },
  divider: {
    height: 1,
    background: 'linear-gradient(90deg, transparent, #E2E8F0, transparent)',
    margin: '28px 0 36px',
  },
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 700,
    fontSize: 20,
    color: '#1E293B',
    margin: '0 0 14px',
    letterSpacing: '-0.01em',
  },
  text: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 400,
    fontSize: 15.5,
    color: '#475569',
    lineHeight: 1.7,
    margin: '0 0 14px',
  },
  list: {
    margin: '0 0 14px',
    paddingLeft: 20,
  },
  listItem: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 400,
    fontSize: 15.5,
    color: '#475569',
    lineHeight: 1.7,
    marginBottom: 8,
  },
  contactCard: {
    background: '#F8FAFC',
    borderRadius: 16,
    padding: '20px 24px',
    marginTop: 12,
  },
  contactItem: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: 15,
    color: '#475569',
    margin: '0 0 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  contactLabel: {
    fontWeight: 600,
    fontSize: 13,
    color: '#94A3B8',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  link: {
    color: '#6366F1',
    textDecoration: 'none',
    fontWeight: 500,
  },
  footer: {
    textAlign: 'center',
    marginTop: 40,
    paddingTop: 24,
    borderTop: '1px solid #F1F5F9',
  },
  footerText: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 400,
    fontSize: 13,
    color: '#CBD5E1',
    margin: 0,
  },
};

export default PrivacyPolicy;
